import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { type FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { selectCompany, selectUser } from '@bbkAdminRedux/app/selectors';
import { isSignedIn } from '@bbkAdminRedux/rtkq/brightback-auth.slice';
import { LogInRouter } from '@bbkAdminComponents/log-in-flow/log-in.router';
import { AppRoot } from '@bbkAdminComponents/app-layout/app-root';
import {
  currentUserSlice,
  useIdentifyUser,
} from '@bbkAdminRedux/rtkq/current-user.slice';
import { useAppInit } from '@bbkAdminRedux/app/hooks';
import { usePollAndRefreshToken } from '@bbkAdminRedux/rtkq/preauth-company.slice';
import { recordError } from '@bbkAdminUtils/recordError';
import { NoApps } from '@bbkAdminRouter/no-apps.page';
import { useQueryFeatureFlags } from '@bbkAdminRedux/rtkq/feature-flags.slice';
import { SuspenseLoader } from '@bbkAdminComponents/loaders/suspense-loader';

export const AuthRoot: React.FC = () => {
  usePollAndRefreshToken();
  useIdentifyUser();
  const user = useSelector(selectUser);
  const company = useSelector(selectCompany);
  const { isLoading: isFFLoading } = useQueryFeatureFlags();

  const [fetchUser$] = currentUserSlice.endpoints.getCurrentUser.useLazyQuery();
  const appInit = useAppInit();

  useEffect(() => {
    fetchUser$(undefined, true)
      .unwrap()
      .then((u) => appInit(u))
      .catch((err: FetchBaseQueryError) => {
        if (!('originalStatus' in err && err.originalStatus === 401)) {
          recordError(err);
          console.log('canceled', err);
        } else {
          appInit(undefined);
        }
      });
  }, [appInit, fetchUser$]);

  const hasToken = useMemo(() => isSignedIn(), []);

  if (isFFLoading) {
    return (
      <div className="tw-h-screen">
        <SuspenseLoader message=" " />;
      </div>
    );
  }

  if (!hasToken) {
    return <LogInRouter />;
  }

  if (company && company.applications.length === 0) {
    return <NoApps company={company} />;
  }

  if (!user) {
    return null;
  }

  return (
    <div className="bbk-authRoot tw-box-content">
      <AppRoot />
    </div>
  );
};
